import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, forkJoin, Observable, of, Subject, Subscription, timer} from 'rxjs';
import {catchError, distinctUntilChanged, map, switchMap} from 'rxjs/operators';
import type {QlQueryDto} from "../../../shared/ql/data/dto/ql-query.dto";
import {QlBuilderModel} from "../../../shared/ql/builder/ql-builder-model";
import {QlBuilderQueryConverter} from "../../../shared/ql/builder/ql-builder-query-converter";
import {QlBuilderRelativeDate} from "../../../shared/ql/builder/ql-builder-relative-date";
import type {QlBuilderDateFieldResolver} from "../../../shared/ql/builder/model/ql-builder-date-field-resolver.type";
import {MetaService} from "../../../shared/meta/service/meta.service";
import {MetaDataEntityDto} from "../../../shared/meta/dto/meta-data-entity.dto";
import {MetaDataAttributeDto} from "../../../shared/meta/dto/meta-data-attribute.dto";
import {QlBuilderKeyLabels} from "../../../shared/ql/component/ql-builder/logic/ql-builder-key-labels";
import {UiQlWidgetService} from "../api/ui-ql-widget.service";
import {UiQlWidgetMapping, UiQlWidgetType} from "../../model/dto/api/ui-ql-widget-mapping.dto";
import {UiQlWidgetDefinitionDto} from "../../model/dto/api/ui-ql-widget-definition.dto";
import {UiQlWidgetSaveRequestDto} from "../../model/dto/api/ui-ql-widget-save-request.dto";
import {UiQlWidgetSaveResponseDto} from "../../model/dto/api/ui-ql-widget-save-response.dto";
import {UiQlWidgetValidateResponseDto} from "../../model/dto/api/ui-ql-widget-validate-response.dto";
import {UiHomeTileSizeEnum} from "../../model/dto/enum/ui-home-tile-size.enum";
import {UiQlWidgetWorkbenchState} from "../../interface/ui-ql-widget-workbench-state.interface";
import {UiQlWidgetWorkbenchMode} from "../../interface/ui-ql-widget-workbench-mode.interface";
import {UiQlWidgetWorkbenchLeftTab} from "../../interface/ui-ql-widget-workbench-left-tab.interface";
import {UiQlWidgetWorkbenchRightTab} from "../../interface/ui-ql-widget-workbench-right-tab.interface";
import {UiQlWidgetWorkbenchDefinitionMeta} from "../../interface/ui-ql-widget-workbench-definition-meta.interface";
import {UiQlWidgetWorkbenchValidation} from "../../interface/ui-ql-widget-workbench-validation.interface";
import {UiQlWidgetSaveDialogValue} from "../../interface/ui-ql-widget-save-dialog-value.interface";
import {UiQlWidgetWorkbenchUndoEntry} from "../../interface/ui-ql-widget-workbench-undo-entry.interface";
import {UiQlAssistChatMessage} from "../../interface/ui-ql-assist-chat-message.interface";
import {UiQlWidgetAssistHistoryEntryDto} from "../../model/dto/api/ui-ql-widget-assist-history-entry.dto";
import {UiQlWidgetWorkbenchHandover} from "../../interface/ui-ql-widget-workbench-handover.interface";

/**
 * Page state of the QL workbench (`UiQlWidgetWorkbenchPage`, modes `builder` | `playground`).
 *
 * <h3>Scope: ONE instance PER PAGE INSTANCE</h3>
 * Deliberately NOT `providedIn: 'root'` — the page lists it in its own `providers`, so every child
 * rendered inside the page (builder tree, chat, preview, QL-JSON, inspector — including content
 * projected into `mvs-review-workbench` panes and the header template) injects the SAME instance,
 * and leaving the page discards it. "Als Widget speichern" (playground -> builder) is a real router
 * navigation (N3: the router must know the current URL, or a later navigation to the builder is
 * ignored and the state of the previous widget survives) - the router rebuilds the page, and the
 * content crosses over through {@link stashHandover} / {@link init}.
 *
 * <h3>Contract for the other specialists</h3>
 * - Read via the observables (`state$`, `model$`, `qlQuery$`, `validation$`, ...) or `snapshot`.
 * - Write ONLY via the setters; never mutate a published `QlBuilderModel` in place — pass a new
 *   object to {@link setModel} (the revision counter and dirty flag hang on that call).
 * - Validation: this service owns ONE debounced policy check (`validate`, `probe=false`, 500 ms,
 *   `switchMap`) once the page called {@link startAutoValidation}. Consumers such as the preview
 *   SHOULD read {@link validation$} / {@link currentValidation$} instead of calling `validate`
 *   themselves; a consumer that still validates on its own pushes its answer via
 *   {@link setValidationResult} so the save gate sees it.
 *
 * <h3>Chat designer (requirement.md "Erweiterung: LLM-Chat-Designer", L4)</h3>
 * - Undo stack: {@link pushUndoSnapshot} before an assistant draft is loaded, {@link undo} for
 *   "Rückgängig", {@link canUndo$}. Only the definition content (model, widget type, mapping, dirty
 *   flag) is stacked - meta, mode and tabs are never touched by an undo.
 * - Conversation: {@link conversationId} (one UUID per page instance, renewed by {@link init} and
 *   {@link reset}), the message list {@link chatMessages$} (kept here so it survives switching the
 *   left tab) and {@link chatHistory} (the at most {@link MAX_CHAT_HISTORY_ENTRIES} last completed
 *   texts sent with the next request). {@link init} and {@link reset} clear list and undo stack.
 */
@Injectable()
export class UiQlWidgetWorkbenchStateService implements OnDestroy {

    /** Debounce of the automatic policy check (requirement.md §Frontend "Vorschau"). */
    static readonly VALIDATE_DEBOUNCE_MS = 500;

    /** Sizes offered by default in the save dialog for a new widget. */
    static readonly DEFAULT_SIZES: UiHomeTileSizeEnum[] = [UiHomeTileSizeEnum.s2x2, UiHomeTileSizeEnum.s4x2];
    static readonly DEFAULT_SIZE: UiHomeTileSizeEnum = UiHomeTileSizeEnum.s2x2;

    /** Most entries the undo stack keeps; older ones are dropped. */
    static readonly MAX_UNDO_ENTRIES = 20;

    /** Most history entries sent with one assist request (requirement.md L3.3: <= 8). */
    static readonly MAX_CHAT_HISTORY_ENTRIES = 8;

    /** Most characters of one history text (requirement.md L3.3: <= 1000). */
    static readonly MAX_CHAT_TEXT_LENGTH = 1000;

    /**
     * The content handed from the playground page instance to the builder page instance (N3). App-wide
     * on purpose: the two instances never exist at the same time, and a page-scoped service cannot
     * outlive its page. Consumed by exactly ONE {@link init}('builder').
     */
    private static pendingHandover: UiQlWidgetWorkbenchHandover | null = null;

    private readonly stateSubject: BehaviorSubject<UiQlWidgetWorkbenchState>;

    /** Emits once whenever the backend refused validate with 401/403 — the page demotes its access. */
    private readonly accessDeniedSubject = new Subject<void>();

    /** The definition as last loaded or saved — what "Zurücksetzen" returns to in builder mode. */
    private baseline: UiQlWidgetDefinitionDto | null = null;

    private validationSubscription: Subscription | null = null;

    /** Definition contents to return to with "Rückgängig", newest LAST. */
    private readonly undoStackSubject = new BehaviorSubject<UiQlWidgetWorkbenchUndoEntry[]>([]);

    /** The chat conversation of this page instance, oldest first. */
    private readonly chatMessagesSubject = new BehaviorSubject<UiQlAssistChatMessage[]>([]);

    private conversationIdValue: string = UiQlWidgetWorkbenchStateService.newConversationId();

    private chatMessageSeq: number = 0;

    readonly state$: Observable<UiQlWidgetWorkbenchState>;
    readonly mode$: Observable<UiQlWidgetWorkbenchMode>;
    readonly model$: Observable<QlBuilderModel | null>;
    readonly widgetType$: Observable<UiQlWidgetType>;
    readonly mapping$: Observable<UiQlWidgetMapping>;
    readonly meta$: Observable<UiQlWidgetWorkbenchDefinitionMeta>;
    readonly dirty$: Observable<boolean>;
    readonly leftTab$: Observable<UiQlWidgetWorkbenchLeftTab>;
    readonly rightTab$: Observable<UiQlWidgetWorkbenchRightTab>;

    /** The last validate answer, whatever revision it belongs to. */
    readonly validation$: Observable<UiQlWidgetWorkbenchValidation | null>;

    /** The validate answer for the CURRENT revision, `null` while pending or stale. */
    readonly currentValidation$: Observable<UiQlWidgetValidateResponseDto | null>;

    readonly validating$: Observable<boolean>;

    /** The `QlQueryDto` of the current model (`QlBuilderQueryConverter.toQlQuery`), `null` when empty/incomplete. */
    readonly qlQuery$: Observable<QlQueryDto | null>;

    /** Builder mode, a model, and a VALID validate answer for exactly the current revision. */
    readonly canSave$: Observable<boolean>;

    readonly accessDenied$: Observable<void>;

    /** `true` while "Rückgängig" has something to restore. */
    readonly canUndo$: Observable<boolean>;

    /** The chat conversation (oldest first). */
    readonly chatMessages$: Observable<UiQlAssistChatMessage[]>;

    constructor(private readonly qlWidgetService: UiQlWidgetService,
                private readonly metaService: MetaService) {

        this.stateSubject = new BehaviorSubject<UiQlWidgetWorkbenchState>(
            UiQlWidgetWorkbenchStateService.createInitialState('builder'));

        this.state$ = this.stateSubject.asObservable();
        this.mode$ = this.select(s => s.mode);
        this.model$ = this.select(s => s.model);
        this.widgetType$ = this.select(s => s.widgetType);
        this.mapping$ = this.select(s => s.mapping);
        this.meta$ = this.select(s => s.meta);
        this.dirty$ = this.select(s => s.dirty);
        this.leftTab$ = this.select(s => s.leftTab);
        this.rightTab$ = this.select(s => s.rightTab);
        this.validation$ = this.select(s => s.validation);
        this.validating$ = this.select(s => s.validating);
        this.currentValidation$ = this.select(s => UiQlWidgetWorkbenchStateService.currentResult(s));
        this.qlQuery$ = this.model$.pipe(map(model => UiQlWidgetWorkbenchStateService.toQlQuery(model)));
        this.canSave$ = this.select(s => UiQlWidgetWorkbenchStateService.computeCanSave(s));
        this.accessDenied$ = this.accessDeniedSubject.asObservable();
        this.canUndo$ = this.undoStackSubject.pipe(map(stack => stack.length > 0), distinctUntilChanged());
        this.chatMessages$ = this.chatMessagesSubject.asObservable();
    }

    // ------------------------------------------------------------------
    // read
    // ------------------------------------------------------------------

    get snapshot(): UiQlWidgetWorkbenchState {
        return this.stateSubject.value;
    }

    /** Synchronous twin of {@link qlQuery$}. */
    buildQlQuery(): QlQueryDto | null {
        return UiQlWidgetWorkbenchStateService.toQlQuery(this.snapshot.model);
    }

    /** Synchronous twin of {@link canSave$}. */
    canSave(): boolean {
        return UiQlWidgetWorkbenchStateService.computeCanSave(this.snapshot);
    }

    // ------------------------------------------------------------------
    // write
    // ------------------------------------------------------------------

    /**
     * Start from scratch in `mode` (called once by the page on init) - or, in builder mode, from the
     * content a playground instance stashed with {@link stashHandover} right before navigating here.
     *
     * @returns `true` when a handover was taken over (the page then skips its route-driven reset)
     */
    init(mode: UiQlWidgetWorkbenchMode): boolean {
        this.baseline = null;
        this.resetConversation();
        const handover = mode === 'builder' ? UiQlWidgetWorkbenchStateService.takeHandover() : null;
        if (!handover) {
            this.stateSubject.next(UiQlWidgetWorkbenchStateService.createInitialState(mode));
            return false;
        }
        this.conversationIdValue = handover.conversationId;
        this.chatMessagesSubject.next([...handover.chatMessages]);
        this.chatMessageSeq = handover.chatMessages.reduce((max, m) => Math.max(max, m.id), 0);
        this.stateSubject.next(this.withRevision({
            ...UiQlWidgetWorkbenchStateService.createInitialState(mode),
            model: handover.model,
            widgetType: handover.widgetType,
            mapping: handover.mapping ?? {},
            dirty: handover.dirty,
            leftTab: handover.leftTab,
        }, 1));
        return true;
    }

    /**
     * Switch the mode of the SAME page instance. Everything else — model, widget type, mapping,
     * dirty flag, validation — is kept. (Kept for tests and the widget-test-shell; the workbench page
     * itself navigates, see {@link stashHandover}.)
     */
    setMode(mode: UiQlWidgetWorkbenchMode): void {
        this.patch({mode});
    }

    /**
     * "Als Widget speichern" (N3): remember this instance's content for the builder page instance the
     * router is about to create. Only the definition content and the chat travel - never a widget id, so
     * the builder starts as "not saved yet" (POST on the first save).
     */
    stashHandover(): void {
        const s = this.snapshot;
        UiQlWidgetWorkbenchStateService.pendingHandover = {
            model: s.model,
            widgetType: s.widgetType,
            mapping: s.mapping ?? {},
            dirty: s.dirty,
            leftTab: s.leftTab,
            chatMessages: [...this.chatMessagesSubject.value],
            conversationId: this.conversationIdValue,
        };
    }

    /** The stashed handover, removed on read; `null` when there is none. */
    static takeHandover(): UiQlWidgetWorkbenchHandover | null {
        const handover = UiQlWidgetWorkbenchStateService.pendingHandover;
        UiQlWidgetWorkbenchStateService.pendingHandover = null;
        return handover;
    }

    /** Replace the builder model (pass a NEW object). Bumps the revision and marks dirty. */
    setModel(model: QlBuilderModel | null): void {
        this.patchContent({model});
    }

    /**
     * A display switch is a CONTENT change (revision bump -> one debounced `validate`), on purpose (QA #25):
     * the save gate ({@link canSave$}) demands a server verdict for exactly the current revision, and the
     * server-side checks that depend on type + mapping (`groupByRequired`, `mappingInvalid`) must gate
     * "Speichern" - a stale query-only verdict must never unlock saving a chart without grouping or a
     * category with a dangling result key. The round trip is `probe=false` (no SQL, the policy check of
     * an in-memory definition) and the 500 ms debounce with `switchMap` already merges the
     * `setWidgetType` + `setMapping` pair of a type click (and the model/type/mapping triple of a preset
     * load) into ONE request - there is no second call to save. Skipping the server for "pure" table/list
     * switches would buy one cheap request and cost a second code path for the gate; not worth it.
     */
    setWidgetType(widgetType: UiQlWidgetType): void {
        this.patchContent({widgetType});
    }

    /** See {@link setWidgetType} for why a mapping change validates (once, debounced). */
    setMapping(mapping: UiQlWidgetMapping): void {
        this.patchContent({mapping: mapping ?? {}});
    }

    setLeftTab(leftTab: UiQlWidgetWorkbenchLeftTab): void {
        this.patch({leftTab});
    }

    setRightTab(rightTab: UiQlWidgetWorkbenchRightTab): void {
        this.patch({rightTab});
    }

    /**
     * Record a validate answer computed OUTSIDE this service (e.g. a preview that validates on its
     * own). `revision` defaults to the current one — pass the revision the request was built from if
     * the answer may arrive late, otherwise a stale answer could unlock saving.
     */
    setValidationResult(result: UiQlWidgetValidateResponseDto, revision: number = this.snapshot.revision): void {
        this.patch({
            validation: {revision, result, source: 'server'},
            validating: revision === this.snapshot.revision ? false : this.snapshot.validating,
            validationError: null,
        });
    }

    /**
     * Load a saved definition (edit route, or "JSON -> Builder" style re-import of a full definition).
     *
     * @returns the converter's `unmappable` messages — a non-empty list means the query could NOT be
     *          represented by the builder; the model is then left `null` and the caller MUST show the
     *          messages (never silently drop parts of a definition).
     */
    applyDefinition(dto: UiQlWidgetDefinitionDto): string[] {

        const converted = dto?.qlQuery
            ? QlBuilderQueryConverter.fromQlQuery(dto.qlQuery)
            : {model: null, unmappable: ['Die Definition enthält keine QL-Abfrage.']};

        const unmappable = converted.unmappable ?? [];

        this.baseline = dto;

        this.stateSubject.next(this.withRevision({
            ...this.snapshot,
            model: unmappable.length ? null : converted.model,
            widgetType: dto.widgetType ?? 'table',
            mapping: dto.mapping ?? {},
            meta: UiQlWidgetWorkbenchStateService.metaFromDefinition(dto),
            dirty: false,
            validation: null,
        }, this.snapshot.revision + 1));

        return unmappable;
    }

    /**
     * Take over a successful activate/deactivate (`PUT …/activate|deactivate`). Only the `hidden` flag
     * changes - content, revision and dirty flag stay, and the reset baseline follows so
     * "Zurücksetzen" does not bring the old flag back.
     */
    markHidden(hidden: boolean): void {

        if (this.baseline) {
            this.baseline = {...this.baseline, hidden};
        }

        this.patch({meta: {...this.snapshot.meta, hidden}});
    }

    /**
     * Take over a successful save. The state is then CLEAN (dirty = false) and a subsequent save is
     * an UPDATE of `response.uiWidgetId`.
     */
    markSaved(response: UiQlWidgetSaveResponseDto, value: UiQlWidgetSaveDialogValue): void {

        const meta: UiQlWidgetWorkbenchDefinitionMeta = {
            uiWidgetId: response.uiWidgetId,
            uiHomeAppId: response.uiHomeAppId,
            alias: response.alias,
            name: value.name,
            description: value.description,
            sizes: [...value.sizes],
            defaultSize: value.defaultSize,
            envBound: !!response.envBound,
            // a save does not (re)activate: the flag stays what the definition said
            hidden: this.snapshot.meta.hidden,
        };

        const qlQuery = this.buildQlQuery();

        this.baseline = qlQuery ? {
            uiWidgetId: meta.uiWidgetId,
            uiHomeAppId: meta.uiHomeAppId,
            alias: meta.alias,
            name: meta.name,
            description: meta.description,
            qlQuery,
            widgetType: this.snapshot.widgetType,
            mapping: this.snapshot.mapping,
            sizes: meta.sizes,
            defaultSize: meta.defaultSize,
            envBound: meta.envBound,
            hidden: meta.hidden,
        } : null;

        this.patch({meta, dirty: false});
    }

    /**
     * "Zurücksetzen": builder mode with a loaded/saved definition returns to it; everything else
     * returns to an empty workbench (the mode is kept).
     */
    reset(): void {

        // "Zurücksetzen" also starts a new chat conversation (new conversationId, empty list, no undo)
        this.resetConversation();

        if (this.snapshot.mode === 'builder' && this.baseline) {
            this.applyDefinition(this.baseline);
            return;
        }

        const mode = this.snapshot.mode;
        const initial = UiQlWidgetWorkbenchStateService.createInitialState(mode);

        this.stateSubject.next(this.withRevision({
            ...initial,
            leftTab: this.snapshot.leftTab,
            rightTab: this.snapshot.rightTab,
        }, this.snapshot.revision + 1));
    }

    /** Build the create/update body from the current state plus the dialog values. */
    buildSaveRequest(value: UiQlWidgetSaveDialogValue): UiQlWidgetSaveRequestDto | null {

        const qlQuery = this.buildQlQuery();

        if (!qlQuery) {
            return null;
        }

        const request: UiQlWidgetSaveRequestDto = {
            name: value.name,
            qlQuery,
            widgetType: this.snapshot.widgetType,
            mapping: this.snapshot.mapping ?? {},
            sizes: [...value.sizes],
            defaultSize: value.defaultSize,
        };

        if (value.description) {
            request.description = value.description;
        }

        // alias is deliberately NOT sent: derived by the server on create, immutable on update.
        return request;
    }

    // ------------------------------------------------------------------
    // undo stack (chat designer)
    // ------------------------------------------------------------------

    /** Synchronous twin of {@link canUndo$}. */
    canUndo(): boolean {
        return this.undoStackSubject.value.length > 0;
    }

    /**
     * Remember the CURRENT definition content (model, widget type, mapping, dirty flag) so that
     * {@link undo} can return to it. Call it right before loading an assistant draft.
     */
    pushUndoSnapshot(): void {

        const s = this.snapshot;
        const entry: UiQlWidgetWorkbenchUndoEntry = {
            model: s.model,
            widgetType: s.widgetType,
            mapping: s.mapping,
            dirty: s.dirty,
        };

        const stack = [...this.undoStackSubject.value, entry];
        this.undoStackSubject.next(stack.slice(-UiQlWidgetWorkbenchStateService.MAX_UNDO_ENTRIES));
    }

    /**
     * "Rückgängig": restore the newest remembered content in ONE revision (one validation, one
     * preview run). Manual builder edits made after that snapshot are reverted with it.
     *
     * @returns `false` when there was nothing to restore
     */
    undo(): boolean {

        const stack = this.undoStackSubject.value;

        if (!stack.length) {
            return false;
        }

        const entry = stack[stack.length - 1];
        this.undoStackSubject.next(stack.slice(0, -1));

        this.stateSubject.next(this.withRevision({
            ...this.snapshot,
            model: entry.model,
            widgetType: entry.widgetType,
            mapping: entry.mapping ?? {},
            dirty: entry.dirty,
        }, this.snapshot.revision + 1));

        return true;
    }

    // ------------------------------------------------------------------
    // chat conversation (chat designer)
    // ------------------------------------------------------------------

    /** UUID of the current conversation - one per page instance, renewed by {@link init} / {@link reset}. */
    get conversationId(): string {
        return this.conversationIdValue;
    }

    /** Synchronous twin of {@link chatMessages$}. */
    get chatMessages(): UiQlAssistChatMessage[] {
        return this.chatMessagesSubject.value;
    }

    /** Append a message; the id is assigned here. */
    addChatMessage(message: Omit<UiQlAssistChatMessage, 'id'>): UiQlAssistChatMessage {
        const added: UiQlAssistChatMessage = {...message, id: ++this.chatMessageSeq};
        this.chatMessagesSubject.next([...this.chatMessagesSubject.value, added]);
        return added;
    }

    /** Replace fields of one message (a NEW object - published messages are never mutated). */
    updateChatMessage(id: number, patch: Partial<Omit<UiQlAssistChatMessage, 'id'>>): void {
        this.chatMessagesSubject.next(
            this.chatMessagesSubject.value.map(m => m.id === id ? {...m, ...patch, id} : m));
    }

    /** Drop one message (e.g. an error entry that is being retried). */
    removeChatMessage(id: number): void {
        this.chatMessagesSubject.next(this.chatMessagesSubject.value.filter(m => m.id !== id));
    }

    /**
     * `history` for the next assist request: the texts of completed turns (`inHistory`), oldest
     * first, the last {@link MAX_CHAT_HISTORY_ENTRIES} only, each cut to {@link MAX_CHAT_TEXT_LENGTH}.
     */
    chatHistory(): UiQlWidgetAssistHistoryEntryDto[] {
        return this.chatMessagesSubject.value
            .filter(m => m.inHistory && (m.role === 'user' || m.role === 'assistant') && !!m.text)
            .slice(-UiQlWidgetWorkbenchStateService.MAX_CHAT_HISTORY_ENTRIES)
            .map(m => ({
                role: m.role as UiQlWidgetAssistHistoryEntryDto['role'],
                text: m.text.slice(0, UiQlWidgetWorkbenchStateService.MAX_CHAT_TEXT_LENGTH),
            }));
    }

    /** New conversation: new UUID, empty message list, empty undo stack. */
    resetConversation(): void {
        this.conversationIdValue = UiQlWidgetWorkbenchStateService.newConversationId();
        this.chatMessagesSubject.next([]);
        this.undoStackSubject.next([]);
    }

    // ------------------------------------------------------------------
    // automatic policy check
    // ------------------------------------------------------------------

    /**
     * Start the ONE debounced policy check of this page (idempotent). On every revision change:
     * wait {@link VALIDATE_DEBOUNCE_MS}, run the client pre-check, and only when that is clean ask
     * `POST /ui/qlWidgets/validate` with `probe=false`. `switchMap` drops answers of older revisions.
     */
    startAutoValidation(): void {

        if (this.validationSubscription) {
            return;
        }

        this.validationSubscription = this.stateSubject.pipe(
            map(s => s.revision),
            distinctUntilChanged(),
            // NO synchronous state write in here: this runs INSIDE a stateSubject emission, and a
            // re-entrant next() would reach later subscribers before the outer (older) value, leaving
            // them on a stale snapshot. The `validating` flag is therefore set by the revision bump
            // itself (withRevision); the only writes below happen after the timer, asynchronously.
            switchMap(revision => {

                const state = this.snapshot;
                const qlQuery = UiQlWidgetWorkbenchStateService.toQlQuery(state.model);

                if (!qlQuery) {
                    return of(null);
                }

                return timer(UiQlWidgetWorkbenchStateService.VALIDATE_DEBOUNCE_MS).pipe(
                    switchMap(() => this.runValidation(revision, qlQuery, state.widgetType, state.mapping)),
                );
            }),
        ).subscribe();

        // the current revision may already need a check (e.g. a definition loaded before the start)
        this.stateSubject.next(this.withRevision({...this.snapshot}, this.snapshot.revision + 1));
    }

    ngOnDestroy(): void {
        this.validationSubscription?.unsubscribe();
        this.validationSubscription = null;
        this.stateSubject.complete();
        this.accessDeniedSubject.complete();
        this.undoStackSubject.complete();
        this.chatMessagesSubject.complete();
    }

    private runValidation(revision: number,
                          qlQuery: QlQueryDto,
                          widgetType: UiQlWidgetType,
                          mapping: UiQlWidgetMapping): Observable<null> {

        const model = this.snapshot.model;

        // the model reveals what the wire shape cannot (e.g. a +/_ node that is a join, spike S2), the
        // metadata which fields are dates - so a relative date on a string field is refused client-side
        return this.resolveDateFields(model).pipe(
            switchMap(dateFields => {

                const clientViolations = QlBuilderQueryConverter.precheckQlQuery(qlQuery, model, dateFields);

                if (clientViolations.length) {
                    this.applyValidation(revision, {
                        valid: false,
                        envBound: model ? QlBuilderQueryConverter.deriveEnvBound(model) : false,
                        violations: clientViolations,
                    }, 'client');
                    return of(null);
                }

                return this.qlWidgetService.validate({qlQuery, widgetType, mapping: mapping ?? {}, probe: false}).pipe(
                    map(result => {
                        this.applyValidation(revision, result, 'server');
                        return null;
                    }),
                    catchError(error => {
                        if (error?.status === 403 || error?.status === 401) {
                            this.accessDeniedSubject.next();
                        }
                        if (revision === this.snapshot.revision) {
                            this.patch({
                                validating: false,
                                validationError: 'Die Definition konnte nicht geprüft werden.',
                            });
                        }
                        return of(null);
                    }),
                );
            }),
        );
    }

    /**
     * The date-field resolver for the client precheck: ONE resolver map over EVERY regular node alias of the
     * definition (root + joins), so a relative date in a query-level condition on a join alias
     * (`j1.createdDate ≥ ${D-TODAY-P30D}`) is typed exactly like one on the root. The joined entities are
     * resolved level by level through the parent's metadata (`QlBuilderKeyLabels.entityAliases`, same rule
     * as the panel's labels); `MetaService` caches every entity, so this is normally synchronous. A node whose
     * entity cannot be resolved or loaded answers `null` (unknown) and stays with the backend. Never fails.
     */
    private resolveDateFields(model: QlBuilderModel | null): Observable<QlBuilderDateFieldResolver | null> {

        const root = model?.root;
        if (!root?.entityName) {
            return of(null);
        }

        const metas = new Map<string, MetaDataEntityDto>();
        const failed = new Set<string>();

        const loadMissing = (): Observable<QlBuilderDateFieldResolver> => {
            const missing = QlBuilderKeyLabels.entityAliases(model, metas).filter(alias => !metas.has(alias) && !failed.has(alias));
            if (!missing.length) {
                return of(UiQlWidgetWorkbenchStateService.dateFieldResolverOf(model, metas));
            }
            return forkJoin(missing.map(alias => this.metaService.getByAlias(alias, true, true, true, true, true).pipe(
                catchError(() => of(null as MetaDataEntityDto | null)),
            ))).pipe(
                switchMap(results => {
                    results.forEach((meta, index) => meta ? metas.set(missing[index], meta) : failed.add(missing[index]));
                    return loadMissing(); // a loaded level may make the next level resolvable
                }),
            );
        };

        return loadMissing().pipe(catchError(() => of(null)));
    }

    /** Resolver map `<node alias> -> attributes of its entity` from the loaded metadata (unresolvable nodes are left out). */
    private static dateFieldResolverOf(model: QlBuilderModel,
                                       metas: ReadonlyMap<string, MetaDataEntityDto>): QlBuilderDateFieldResolver {
        const attributesByAlias = new Map<string, MetaDataAttributeDto[] | null | undefined>();
        for (const [alias, info] of QlBuilderKeyLabels.nodeInfo(model, metas)) {
            const meta = info.entityAlias ? metas.get(info.entityAlias) : null;
            if (meta) {
                attributesByAlias.set(alias, meta.attributes);
            }
        }
        return QlBuilderRelativeDate.dateFieldResolver(attributesByAlias);
    }

    private applyValidation(revision: number,
                            result: UiQlWidgetValidateResponseDto,
                            source: 'client' | 'server'): void {

        if (revision !== this.snapshot.revision) {
            return; // stale - a newer revision is already on its way
        }

        this.patch({
            validation: {revision, result, source},
            validating: false,
            validationError: null,
        });
    }

    // ------------------------------------------------------------------
    // helpers
    // ------------------------------------------------------------------

    private patch(partial: Partial<UiQlWidgetWorkbenchState>): void {
        this.stateSubject.next({...this.snapshot, ...partial});
    }

    /** A change of the definition content: bump the revision and mark dirty. */
    private patchContent(partial: Partial<UiQlWidgetWorkbenchState>): void {
        this.stateSubject.next(this.withRevision({
            ...this.snapshot,
            ...partial,
            dirty: true,
        }, this.snapshot.revision + 1));
    }

    /**
     * Stamp a new revision onto `next` and derive `validating` for it: pending exactly when the
     * automatic check runs and the model yields a query.
     */
    private withRevision(next: UiQlWidgetWorkbenchState, revision: number): UiQlWidgetWorkbenchState {
        const pending = !!this.validationSubscription
            && UiQlWidgetWorkbenchStateService.toQlQuery(next.model) !== null;
        return {...next, revision, validating: pending, validationError: null};
    }

    private select<T>(projector: (state: UiQlWidgetWorkbenchState) => T): Observable<T> {
        return this.stateSubject.pipe(map(projector), distinctUntilChanged());
    }

    private static toQlQuery(model: QlBuilderModel | null): QlQueryDto | null {

        if (!model?.root?.entityName) {
            return null;
        }

        try {
            return QlBuilderQueryConverter.toQlQuery(model);
        } catch (e) {
            // an incomplete tree mid-edit is not an error worth surfacing - it simply has no query yet
            return null;
        }
    }

    /** RFC 4122 v4 UUID; `crypto.randomUUID` needs a secure context, hence the fallback. */
    private static newConversationId(): string {

        const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;

        if (c && typeof c.randomUUID === 'function') {
            return c.randomUUID();
        }

        const bytes = new Uint8Array(16);
        if (c && typeof c.getRandomValues === 'function') {
            c.getRandomValues(bytes);
        } else {
            for (let i = 0; i < bytes.length; i++) {
                bytes[i] = Math.floor(Math.random() * 256);
            }
        }
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    private static currentResult(state: UiQlWidgetWorkbenchState): UiQlWidgetValidateResponseDto | null {
        return state.validation && state.validation.revision === state.revision
            ? state.validation.result
            : null;
    }

    private static computeCanSave(state: UiQlWidgetWorkbenchState): boolean {
        const current = UiQlWidgetWorkbenchStateService.currentResult(state);
        return state.mode === 'builder'
            && !!state.model
            && !state.validating
            && current?.valid === true;
    }

    private static metaFromDefinition(dto: UiQlWidgetDefinitionDto): UiQlWidgetWorkbenchDefinitionMeta {
        return {
            uiWidgetId: dto.uiWidgetId ?? null,
            uiHomeAppId: dto.uiHomeAppId ?? null,
            alias: dto.alias ?? null,
            name: dto.name ?? '',
            description: dto.description ?? '',
            sizes: dto.sizes?.length ? [...dto.sizes] : [...UiQlWidgetWorkbenchStateService.DEFAULT_SIZES],
            defaultSize: dto.defaultSize ?? UiQlWidgetWorkbenchStateService.DEFAULT_SIZE,
            envBound: !!dto.envBound,
            hidden: !!dto.hidden,
        };
    }

    private static createInitialState(mode: UiQlWidgetWorkbenchMode): UiQlWidgetWorkbenchState {
        return {
            mode,
            model: null,
            widgetType: 'table',
            mapping: {},
            meta: {
                uiWidgetId: null,
                uiHomeAppId: null,
                alias: null,
                name: '',
                description: '',
                sizes: [...UiQlWidgetWorkbenchStateService.DEFAULT_SIZES],
                defaultSize: UiQlWidgetWorkbenchStateService.DEFAULT_SIZE,
                envBound: false,
                hidden: false,
            },
            dirty: false,
            revision: 0,
            validation: null,
            validating: false,
            validationError: null,
            leftTab: 'builder',
            rightTab: 'preview',
        };
    }
}
